import React, { useRef, useEffect, useMemo, useState } from 'react';
import { Table, TableBody, TableRow, TableCell, TableHead, Chip, Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  AccountBalance as AccountBalanceIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
} from '@mui/icons-material';
import { isProjectedMonth, projectedMonthLabel } from '../../utils/projectedMonth';

const BudgetTableView = ({
  displayMonths,
  accounts,
  budgetData,
  forecastData,
  forecastMonths,
  showForecast,
  isForecastMonth,
  displayOptions,
  getBudgetTarget,
  getVarianceChipProps,
  getMomChipProps,
  formatCurrency,
  formatPercentage,
  getCategoryColor,
  allAccountBalancesByMonth,
  monthlyForecastBalances,
  handleAccountBalanceClick,
  handleCategoryClick,
  sortedIncomeCategories,
  sortedExpenseCategories,
  categories = [],
  calculateAccountBalanceDetails,
  budgetTotals,
  theme,
  compact = false
}) => {
  const floatScrollbarRef = useRef(null);
  const floatScrollbarInnerRef = useRef(null);
  const tableContainerRef = useRef(null);

  // Build a lookup map from category ID to display name
  const categoryNameMap = React.useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.id] = c.name; });
    return map;
  }, [categories]);
  const getCategoryDisplayName = (id) => categoryNameMap[id] || (id === 'uncategorized' ? 'Uncategorized' : id);

  // Each category's own colour (as set in the Categories manager) tints its row-label cell
  const categoryColorMap = React.useMemo(() => {
    const map = {};
    categories.forEach(c => { if (c.color) map[c.id] = c.color; });
    return map;
  }, [categories]);
  const getCategoryTint = (id) => categoryColorMap[id] || theme.palette.grey[400];

  // Category label cell tinted with the category's colour. The tint is blended
  // over white (not plain alpha) so the sticky column stays opaque while month
  // cells scroll beneath it.
  const categoryLabelCellSx = (category) => {
    const tint = getCategoryTint(category);
    return {
      position: 'sticky',
      left: 0,
      zIndex: 3,
      borderRight: `1px solid ${theme.palette.divider}`,
      background: `linear-gradient(${alpha(tint, 0.14)}, ${alpha(tint, 0.14)}), ${theme.palette.background.paper}`,
      'tr:hover &': {
        background: `linear-gradient(${alpha(tint, 0.24)}, ${alpha(tint, 0.24)}), ${theme.palette.background.paper}`,
      },
    };
  };

  // Account label cell tinted with the account's colour, same treatment as
  // categoryLabelCellSx above.
  const accountLabelCellSx = (account) => {
    const tint = account.color || theme.palette.grey[400];
    return {
      position: 'sticky',
      left: 0,
      zIndex: 3,
      borderRight: `1px solid ${theme.palette.divider}`,
      background: `linear-gradient(${alpha(tint, 0.14)}, ${alpha(tint, 0.14)}), ${theme.palette.background.paper}`,
      'tr:hover &': {
        background: `linear-gradient(${alpha(tint, 0.24)}, ${alpha(tint, 0.24)}), ${theme.palette.background.paper}`,
      },
    };
  };

  // Identify the live month (e.g. "Jun-26") so its column can be highlighted
  const now = new Date();
  const currentMonthLabel = `${now.toLocaleString('en-US', { month: 'short' })}-${String(now.getFullYear()).slice(-2)}`.toLowerCase();
  const isCurrentMonth = (m) => (m || '').toLowerCase() === currentMonthLabel;
  // Faint tint marking the current month's cells, applied the whole way down
  // the column (skipped on forecast cells, which carry their own tint).
  // `base` is for rows that already have a fill of their own — the dark Net
  // Income band — where the tint is laid over that fill rather than replacing
  // it, at a heavier alpha so it still reads against a dark ground.
  const currentMonthCellSx = (month, base) => {
    if (!isCurrentMonth(month) || isForecastMonth(month)) return {};
    if (!base) return { backgroundColor: alpha(theme.palette.primary.main, 0.05) };
    const tint = alpha(theme.palette.primary.main, 0.22);
    return { background: `linear-gradient(${tint}, ${tint}), ${base}` };
  };

  // Slim, quiet scrollbar shared by the table and the synthetic top bar
  const slimScrollbarSx = {
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.grey[400]} transparent`,
    '&::-webkit-scrollbar': { width: '8px', height: '8px' },
    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
    '&::-webkit-scrollbar-thumb': { backgroundColor: theme.palette.grey[300], borderRadius: '8px' },
    '&::-webkit-scrollbar-thumb:hover': { backgroundColor: theme.palette.grey[400] },
  };

  // Soften the vs-Bud / MoM variance pills: tinted backgrounds instead of solid fills
  const softPillSx = {
    '& .MuiChip-filledSuccess': {
      backgroundColor: alpha(theme.palette.success.main, 0.15),
      color: theme.palette.success.dark,
    },
    '& .MuiChip-filledError': {
      backgroundColor: alpha(theme.palette.error.main, 0.15),
      color: theme.palette.error.dark,
    },
    '& .MuiChip-filledSuccess .MuiChip-icon, & .MuiChip-filledError .MuiChip-icon': {
      color: 'inherit',
    },
  };

  useEffect(() => {
    const floatBarEl = floatScrollbarRef.current;
    const floatInnerEl = floatScrollbarInnerRef.current;
    const tableContainerEl = tableContainerRef.current;

    if (!floatBarEl || !floatInnerEl || !tableContainerEl) return;

    // The bar is fixed to the viewport bottom; it fades in while the tables are
    // on screen (stronger on hover) and disappears when there's nothing to scroll.
    let inView = false;
    let hovering = false;
    let hasOverflow = false;

    const applyVisibility = () => {
        const show = inView && hasOverflow;
        floatBarEl.style.opacity = show ? (hovering ? '1' : '0.5') : '0';
        floatBarEl.style.pointerEvents = show ? 'auto' : 'none';
    };

    const syncScrollbar = () => {
        // Get the table element inside the container
        const tableEl = tableContainerEl.querySelector('table');
        if (!tableEl) return;

        // Pin the fixed bar to the table area's horizontal footprint
        const rect = tableContainerEl.getBoundingClientRect();
        floatBarEl.style.left = `${rect.left}px`;
        floatBarEl.style.width = `${rect.width}px`;

        // Mirror the table's scrollable distance into the floating bar
        hasOverflow = tableEl.scrollWidth > tableContainerEl.clientWidth;
        if (hasOverflow) {
            floatInnerEl.style.width = `${tableEl.scrollWidth}px`;
        }
        applyVisibility();
    };
    syncScrollbar();

    const handleEnter = () => { hovering = true; applyVisibility(); };
    const handleLeave = () => { hovering = false; applyVisibility(); };
    tableContainerEl.addEventListener('mouseenter', handleEnter);
    tableContainerEl.addEventListener('mouseleave', handleLeave);
    floatBarEl.addEventListener('mouseenter', handleEnter);
    floatBarEl.addEventListener('mouseleave', handleLeave);

    const intersectionObserver = new IntersectionObserver((entries) => {
        inView = entries[0].isIntersecting;
        applyVisibility();
    });
    intersectionObserver.observe(tableContainerEl);

    const handleBarScroll = () => {
        if (Math.abs(tableContainerEl.scrollLeft - floatBarEl.scrollLeft) > 1) {
            tableContainerEl.scrollLeft = floatBarEl.scrollLeft;
        }
    };

    const handleTableScroll = () => {
        if (Math.abs(tableContainerEl.scrollLeft - floatBarEl.scrollLeft) > 1) {
            floatBarEl.scrollLeft = tableContainerEl.scrollLeft;
        }
    };

    floatBarEl.addEventListener('scroll', handleBarScroll);
    tableContainerEl.addEventListener('scroll', handleTableScroll);

    const observer = new MutationObserver(syncScrollbar);
    observer.observe(tableContainerEl, { childList: true, subtree: true, characterData: true });

    window.addEventListener('resize', syncScrollbar);

    return () => {
        floatBarEl.removeEventListener('scroll', handleBarScroll);
        tableContainerEl.removeEventListener('scroll', handleTableScroll);
        tableContainerEl.removeEventListener('mouseenter', handleEnter);
        tableContainerEl.removeEventListener('mouseleave', handleLeave);
        floatBarEl.removeEventListener('mouseenter', handleEnter);
        floatBarEl.removeEventListener('mouseleave', handleLeave);
        intersectionObserver.disconnect();
        observer.disconnect();
        window.removeEventListener('resize', syncScrollbar);
    };
  }, []);
  // Styled header cell
  const cellPadding = compact ? '4px 8px' : '6px 12px';
  // Matrix type scale: amounts/labels are the hero at 14px, section/total headers
  // match them, while metric labels + chips stay small at 12px so the coloured
  // pills read as secondary annotations rather than competing with the figures.
  const headerFontSize = '0.875rem';  // section + total headers
  const firstColWidth = '136px';
  const monthColWidth = compact ? '124px' : '134px';
  // Fixed column widths keep the three section tables perfectly aligned under the shared scrollbar
  const tableMinWidth = parseInt(firstColWidth, 10) + displayMonths.length * parseInt(monthColWidth, 10);
  const bandHeight = compact ? 38 : 44; // group-header band above each section's month row
  const monthColBorder = `1px solid ${alpha(theme.palette.common.black, 0.06)}`;
  const valueFontSize = '0.875rem';  // amounts + row labels
  const chipHeight = 20;
  const chipFont = '0.75rem';  // metric labels (vs Bud / MoM / 3M) + chips
  // Consistent label styling for secondary metric labels (Prop %, vs Bud, MoM)
  const metricLabelBaseSx = { mr: 1, width: '46px', textAlign: 'right', fontSize: chipFont, whiteSpace: 'nowrap' };

  // --- Helpers for the Account Balances section metrics (Prop% / MoM / 3M) ---
  // Resolve a single account's balance for a month (projected ending balance on forecast months)
  const getAccountBalanceFor = (accountId, month) => {
    if (isForecastMonth(month) && monthlyForecastBalances && monthlyForecastBalances[month]) {
      const d = calculateAccountBalanceDetails(accountId, month);
      return d.endingBalance !== undefined ? d.endingBalance : (accounts.find(a => a.id === accountId)?.balance ?? 0);
    }
    return allAccountBalancesByMonth[month]?.[accountId]?.balance ?? 0;
  };
  const getNetWorthFor = (month) => accounts.reduce((sum, a) => sum + getAccountBalanceFor(a.id, month), 0);

  // Clicking a month header sorts that section's rows by their amount in that
  // month; clicking the same month cycles desc -> asc -> back to original order.
  const [sortConfig, setSortConfig] = useState({ income: null, expense: null, balance: null });
  const handleSort = (section, month) => {
    setSortConfig(prev => {
      const current = prev[section];
      if (!current || current.month !== month) return { ...prev, [section]: { month, direction: 'desc' } };
      if (current.direction === 'desc') return { ...prev, [section]: { month, direction: 'asc' } };
      return { ...prev, [section]: null };
    });
  };
  const applySort = (list, sort, getAmount) => {
    if (!sort) return list;
    const sorted = [...list].sort((a, b) => getAmount(a) - getAmount(b));
    if (sort.direction === 'desc') sorted.reverse();
    return sorted;
  };
  const getIncomeAmount = (category, month) => (isForecastMonth(month)
    ? forecastData.incomeCategories[category]?.[month]
    : budgetData.incomeCategories[category]?.[month]) || 0;
  const getExpenseAmount = (category, month) => (isForecastMonth(month)
    ? forecastData.expenseCategories[category]?.[month]
    : budgetData.expenseCategories[category]?.[month]) || 0;
  const displayIncomeCategories = useMemo(
    () => applySort(sortedIncomeCategories, sortConfig.income, (c) => getIncomeAmount(c, sortConfig.income.month)),
    [sortedIncomeCategories, sortConfig.income, budgetData, forecastData]
  );
  const displayExpenseCategories = useMemo(
    () => applySort(sortedExpenseCategories, sortConfig.expense, (c) => getExpenseAmount(c, sortConfig.expense.month)),
    [sortedExpenseCategories, sortConfig.expense, budgetData, forecastData]
  );
  const activeAccounts = useMemo(() => accounts.filter(acc => acc.isActive), [accounts]);
  const displayAccounts = useMemo(
    () => applySort(activeAccounts, sortConfig.balance, (a) => getAccountBalanceFor(a.id, sortConfig.balance.month)),
    [activeAccounts, sortConfig.balance, allAccountBalancesByMonth, monthlyForecastBalances]
  );
  // Previous / prior-3 ACTUAL months from the full data set (so the first visible column still has a prior month)
  const prevActualMonth = (month) => {
    const i = budgetData.months.indexOf(month);
    return i > 0 ? budgetData.months[i - 1] : null;
  };
  const prev3ActualMonths = (month) => {
    const i = budgetData.months.indexOf(month);
    return i >= 0 ? budgetData.months.slice(Math.max(0, i - 3), i) : [];
  };
  // Small renderers matching the income/expense metric rows
  const MetricRow = ({ label, children }) => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>{label}</Typography>
      {children}
    </Box>
  );
  const dashChip = () => (
    <Chip label="-" size="small" color="default" sx={{ height: `${chipHeight}px`, '& .MuiChip-label': { fontSize: chipFont, padding: compact ? '0 3px' : '0 4px' } }} />
  );
  const momChip = (variance) => {
    const cp = getMomChipProps(variance, false);
    return <Chip {...cp} size="small" sx={{ height: `${chipHeight}px`, '& .MuiChip-label': { fontSize: chipFont, padding: compact ? '0 3px' : '0 4px' }, '& .MuiChip-icon': { fontSize: compact ? '0.875rem' : '0.9rem', marginLeft: compact ? '2px' : '4px', marginRight: cp.icon ? '-2px' : 0 } }} />;
  };
  const outlineChip = (label) => (
    <Chip label={label} size="small" variant="outlined" sx={{ borderRadius: '14px', height: `${chipHeight}px`, '& .MuiChip-label': { fontSize: chipFont, padding: compact ? '0 6px' : '0 8px' } }} />
  );

  // Auto-center the current month horizontally (run once per mount)
  useEffect(() => {
    const containerEl = tableContainerRef.current;
    const floatBarEl = floatScrollbarRef.current;
    if (!containerEl || !floatBarEl || !displayMonths?.length) return;
    if (containerEl.getAttribute('data-initial-centered')) return; // already done

    const now = new Date();
    const currentMonthAbbrev = now.toLocaleString('default', { month: 'short' }).toLowerCase();
    let targetIndex = displayMonths.findIndex(m => m.toLowerCase().includes(currentMonthAbbrev));
    if (targetIndex === -1) targetIndex = displayMonths.length - 1; // fallback to last month in list

    const centerScroll = () => {
      const targetCell = containerEl.querySelector(`[data-month-index='${targetIndex}']`);
      if (!targetCell) return;
      const cellWidth = targetCell.getBoundingClientRect().width;
      const containerWidth = containerEl.clientWidth;
      const cellOffsetLeft = targetCell.offsetLeft; // relative to scroll container content
      let desired = cellOffsetLeft - (containerWidth / 2 - cellWidth / 2);
      const max = containerEl.scrollWidth - containerWidth;
      if (desired < 0) desired = 0;
      if (desired > max) desired = max;
      containerEl.scrollLeft = desired;
      floatBarEl.scrollLeft = desired;
      containerEl.setAttribute('data-initial-centered', 'true');
    };
    // Defer until after layout paints (double rAF for safety after dynamic width calc)
    requestAnimationFrame(() => requestAnimationFrame(centerScroll));
  }, [displayMonths]);

  // Each section renders as its own rounded, bordered card (ClickUp-style group)
  const sectionCardSx = {
    width: 'fit-content',
    minWidth: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '12px',
    backgroundColor: theme.palette.background.paper,
    mb: compact ? 2 : 2.5,
  };

  // Shared table settings: fixed layout + explicit col widths keep every
  // section's columns aligned so the shared horizontal scrollbar lines up.
  const tableSx = {
    tableLayout: 'fixed',
    width: `${tableMinWidth}px`,
    minWidth: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    ...softPillSx,
  };

  const renderColGroup = () => (
    <colgroup>
      <col style={{ width: firstColWidth }} />
      {displayMonths.map(month => (
        <col key={month} style={{ width: monthColWidth }} />
      ))}
    </colgroup>
  );

  // Group-header band: icon + section title + row count, pinned to the top of
  // the scroll viewport while its section is in view; the label itself sticks
  // to the left edge so it stays readable during horizontal scrolling.
  const renderSectionBand = (title, color, IconComponent, count) => (
    <TableRow>
      <TableCell
        colSpan={displayMonths.length + 1}
        sx={{
          padding: 0,
          height: `${bandHeight}px`,
          backgroundColor: theme.palette.grey[50],
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
        }}
      >
        <Box sx={{ position: 'sticky', left: 0, width: 'fit-content', display: 'flex', alignItems: 'center', gap: 1, px: 1.5, height: '100%' }}>
          <Box
            component="span"
            sx={{
              width: 24,
              height: 24,
              // Rounded square to match the MetricCard avatars elsewhere on
              // the Analysis page (same corner-to-size ratio as 50px/16px).
              borderRadius: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              bgcolor: alpha(color, 0.12),
              color,
            }}
          >
            <IconComponent sx={{ fontSize: 16 }} />
          </Box>
          <Typography component="span" sx={{ fontWeight: 700, fontSize: headerFontSize, color: 'text.primary' }}>
            {title}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  // Quiet month column headers under the band; the live month gets an accent pill.
  // When sectionKey is given, clicking a month header sorts that section's rows
  // by their amount for that month (click again to flip direction).
  const renderMonthHeaderRow = (keyPrefix, firstColLabel, sectionKey) => (
    <TableRow>
      <TableCell
        sx={{
          position: 'sticky',
          left: 0,
          zIndex: 3,
          backgroundColor: theme.palette.background.paper,
          padding: cellPadding,
          fontWeight: 600,
          fontSize: chipFont,
          color: 'text.secondary',
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderRight: `1px solid ${theme.palette.divider}`,
        }}
      >
        {firstColLabel}
      </TableCell>
      {displayMonths.map((month, index) => {
        const sort = sectionKey ? sortConfig[sectionKey] : null;
        const isSorted = sort && sort.month === month;
        return (
          <TableCell
            key={`${keyPrefix}-month-label-${month}`}
            align="center"
            data-month-index={index}
            data-month-label={month}
            onClick={sectionKey ? () => handleSort(sectionKey, month) : undefined}
            sx={{
              backgroundColor: theme.palette.background.paper,
              padding: cellPadding,
              fontWeight: 600,
              fontSize: chipFont,
              color: 'text.secondary',
              borderBottom: `1px solid ${theme.palette.divider}`,
              borderLeft: monthColBorder,
              ...(isForecastMonth(month) ? { fontStyle: 'italic' } : {}),
              // The live month's header carries the same faint tint as its
              // data cells, so every section's header reads the same way.
              ...(isCurrentMonth(month) && !isForecastMonth(month)
                ? { ...currentMonthCellSx(month), fontWeight: 700 }
                : {}),
              ...(sectionKey ? {
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { backgroundColor: theme.palette.grey[100] },
              } : {}),
            }}
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
              {isProjectedMonth(month) ? projectedMonthLabel(month) : month}
              {isSorted && (
                sort.direction === 'asc'
                  ? <ArrowUpwardIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                  : <ArrowDownwardIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              )}
            </Box>
          </TableCell>
        );
      })}
    </TableRow>
  );

  // Styled total row; `last` rounds the card's bottom corners and drops the trailing divider
  const TotalRow = ({ last, ...props }) => (
    <TableRow
      {...props}
      sx={{
        backgroundColor: theme.palette.grey[50],
        fontWeight: 'bold',
        '& .MuiTableCell-root': {
          fontWeight: 'bold',
          borderTop: `1px solid ${theme.palette.grey[300]}`,
          borderBottom: last ? 0 : `1px solid ${theme.palette.grey[300]}`,
          fontSize: headerFontSize,
          padding: cellPadding,
        },
        ...(last ? {
          '& .MuiTableCell-root:first-of-type': { borderBottomLeftRadius: '12px' },
          '& .MuiTableCell-root:last-of-type': { borderBottomRightRadius: '12px' },
        } : {}),
        '&:hover': {
          backgroundColor: theme.palette.grey[100],
        },
        ...props.sx
      }}
    />
  );

  return (
    <Box sx={{
      mt: 3,
      [theme.breakpoints.down('sm')]: {
        maxWidth: '89vw', // Use more of the viewport width on mobile
      },
      [theme.breakpoints.up('sm')]: {
        maxWidth: '98vw', // Use almost full viewport width on larger screens
      }
    }}>
      <Box sx={{
        [theme.breakpoints.down('sm')]: {
          minWidth: 'auto', // Or a value that fits your content
        }
      }}>
        <Box
          ref={tableContainerRef}
          sx={{
            // Vertical scrolling is left to the page; this container only pans horizontally.
            // Its native scrollbar is hidden — the floating bar below is the visible one.
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {/* INCOME SECTION */}
          <Box sx={sectionCardSx}>
          <Table size="small" sx={tableSx}>
            {renderColGroup()}
            <TableHead>
              {renderSectionBand('Income', theme.palette.success.main, ArrowUpwardIcon, sortedIncomeCategories.length)}
              {renderMonthHeaderRow('income', 'Category', 'income')}
            </TableHead>
            <TableBody>
              {/* INCOME CATEGORIES */}
              {displayIncomeCategories.map(category => (
                <TableRow key={`income-${category}`} hover>
                  <TableCell sx={categoryLabelCellSx(category)}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: valueFontSize, fontWeight: 600 }}>
                        {getCategoryDisplayName(category)}
                      </Typography>
                    </Box>
                  </TableCell>
                  {displayMonths.map((month, index) => {
                    const currentAmount = isForecastMonth(month)
                      ? forecastData.incomeCategories[category]?.[month] || 0
                      : budgetData.incomeCategories[category]?.[month] || 0;
                    return (
                      <TableCell
                        key={`income-${category}-${month}`}
                        align="right"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCategoryClick(category, month, 'Income');
                        }}
                        sx={{
                          cursor: 'pointer',
                          borderLeft: monthColBorder,
                          minWidth: monthColWidth,
                          ...currentMonthCellSx(month),
                          ...(isForecastMonth(month)
                            ? { backgroundColor: 'rgba(156, 39, 176, 0.1)' }
                            : {}),
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: valueFontSize }}>
                            {formatCurrency(currentAmount)}
                          </Typography>
                          {/* Display options with labels - more compact */}
                          <Box sx={{ mt: 0.5 }}>
                            {displayOptions.includes('percentages') && budgetData.monthlySummary[month]?.income > 0 && (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.3 }}>
                                <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>
                                  Prop%:
                                </Typography>
                                <Chip
                                  label={formatPercentage(currentAmount, budgetData.monthlySummary[month].income)}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    borderRadius: '14px',
                                    height: `${chipHeight}px`,
                                    '& .MuiChip-label': {
                                      fontSize: chipFont,
                                      padding: compact ? '0 6px' : '0 8px'
                                    }
                                  }}
                                />
                              </Box>
                            )}
                            {displayOptions.includes('vs-budget') && (
                              (() => {
                                if (isForecastMonth(month)) {
                                  return (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.3 }}>
                                      <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>vs Bud:</Typography>
                                      <Chip label="-" size="small" color="default" sx={{ height: `${chipHeight}px`, '& .MuiChip-label': { fontSize: chipFont, padding: compact ? '0 3px' : '0 4px' } }} />
                                    </Box>
                                  );
                                }
                                const budgetTarget = getBudgetTarget(category, true, month);
                                const variance = budgetTarget === 0
                                  ? (currentAmount > 0 ? '' : '')
                                  : ((currentAmount - budgetTarget) / budgetTarget) * 100;
                                const chipProps = getVarianceChipProps(variance, true);
                                return (
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.3 }}>
                                    <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>
                                      vs Bud:
                                    </Typography>
                                    <Chip
                                      {...chipProps}
                                      size="small"
                                      sx={{
                                        height: `${chipHeight}px`,
                                        '& .MuiChip-label': {
                                          fontSize: chipFont,
                                          padding: compact ? '0 3px' : '0 4px'
                                        },
                                        '& .MuiChip-icon': {
                                          fontSize: compact ? '0.875rem' : '0.9rem',
                                          marginLeft: compact ? '2px' : '4px',
                                          marginRight: chipProps.icon ? '-2px' : 0
                                        }
                                      }}
                                    />
                                  </Box>
                                );
                              })()
                            )}
                            {displayOptions.includes('vs-prior-month') && (
                              (() => {
                                if (isForecastMonth(month)) {
                                  return (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.3 }}>
                                      <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>MoM:</Typography>
                                      <Chip label="-" size="small" color="default" sx={{ height: `${chipHeight}px`, '& .MuiChip-label': { fontSize: chipFont, padding: compact ? '0 3px' : '0 4px' } }} />
                                    </Box>
                                  );
                                }
                                const monthIndex = budgetData.months.indexOf(month);
                                const prevMonth = budgetData.months[monthIndex - 1];
                                const prevAmount = budgetData.incomeCategories[category]?.[prevMonth] || 0;
                                const variance = prevAmount === 0
                                  ? (currentAmount > 0 ? '' : '')
                                  : ((currentAmount - prevAmount) / prevAmount) * 100;
                                const chipProps = getMomChipProps(variance, false);
                                return (
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.3 }}>
                                    <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>
                                      MoM:
                                    </Typography>
                                    <Chip
                                      {...chipProps}
                                      size="small"
                                      sx={{
                                        height: `${chipHeight}px`,
                                        '& .MuiChip-label': {
                                          fontSize: chipFont,
                                          padding: compact ? '0 3px' : '0 4px'
                                        },
                                        '& .MuiChip-icon': {
                                          fontSize: compact ? '0.875rem' : '0.9rem',
                                          marginLeft: compact ? '2px' : '4px',
                                          marginRight: chipProps.icon ? '-2px' : 0
                                        }
                                      }}
                                    />
                                  </Box>
                                );
                              })()
                            )}
                            {displayOptions.includes('3m-avg') && (
                              (() => {
                                if (isForecastMonth(month)) {
                                  return (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.3 }}>
                                      <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>3M:</Typography>
                                      <Chip label="-" size="small" color="default" sx={{ height: `${chipHeight}px`, '& .MuiChip-label': { fontSize: chipFont, padding: compact ? '0 3px' : '0 4px' } }} />
                                    </Box>
                                  );
                                }
                                const monthIndex = budgetData.months.indexOf(month);
                                const prev3 = budgetData.months.slice(Math.max(0, monthIndex - 3), monthIndex);
                                const avg = prev3.length > 0 ? prev3.reduce((sum, m) => sum + (budgetData.incomeCategories[category]?.[m] || 0), 0) / prev3.length : 0;
                                return (
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.3 }}>
                                    <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>
                                      3M:
                                    </Typography>
                                    <Chip
                                      label={formatCurrency(avg)}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        borderRadius: '14px',
                                        height: `${chipHeight}px`,
                                        '& .MuiChip-label': {
                                          fontSize: chipFont,
                                          padding: compact ? '0 6px' : '0 8px'
                                        }
                                      }}
                                    />
                                  </Box>
                                );
                              })()
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              {/* INCOME TOTAL */}
              <TotalRow last>
                <TableCell 
                  sx={{ 
                    fontWeight: 'bold', 
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                    position: 'sticky',
                    left: 0,
                    zIndex: 3,
                    minWidth: firstColWidth,
                    width: firstColWidth,
                    bgcolor: theme.palette.grey[50]
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Total Income
                  </Box>
                </TableCell>
                {displayMonths.map((month, index) => {
                  const currentTotalIncome = isForecastMonth(month)
                    ? forecastData.monthlySummary[month]?.income || 0
                    : budgetData.monthlySummary[month]?.income || 0;
                  const monthIndex = budgetData.months.indexOf(month);
                  const prevMonth = monthIndex > 0 ? budgetData.months[monthIndex - 1] : null;
                  const prevTotalIncome = prevMonth ? budgetData.monthlySummary[prevMonth]?.income || 0 : 0;
                  const totalIncomeTrend = prevTotalIncome === 0
                    ? (currentTotalIncome > 0 ? 100 : 0)
                    : ((currentTotalIncome - prevTotalIncome) / prevTotalIncome) * 100;
                  return (
                    <TableCell key={`income-total-${month}`} align="right" sx={{
                      borderLeft: monthColBorder,
                      fontWeight: 'bold',
                      color: isForecastMonth(month) ? 'secondary.dark' : 'success.dark',
                      minWidth: monthColWidth,
                      ...(isForecastMonth(month) ? { backgroundColor: 'rgba(156, 39, 176, 0.1)' } : {}),
                      ...currentMonthCellSx(month)
                    }}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: valueFontSize }}>
                          {formatCurrency(currentTotalIncome)}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {displayOptions.includes('vs-prior-month') && (
                            (() => {
                              if (isForecastMonth(month)) {
                                return (
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.3 }}>
                                    <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>MoM:</Typography>
                                    <Chip label="-" size="small" color="default" sx={{ height: `${chipHeight}px`, '& .MuiChip-label': { fontSize: chipFont, padding: compact ? '0 3px' : '0 4px' } }} />
                                  </Box>
                                );
                              }
                              if (!prevMonth) return null;
                              const chipProps = getMomChipProps(totalIncomeTrend, false);
                              return (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.3 }}>
                                  <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>
                                    MoM:
                                  </Typography>
                                  <Chip
                                    {...chipProps}
                                    size="small"
                                    sx={{
                                      height: `${chipHeight}px`,
                                      '& .MuiChip-label': {
                                        fontSize: chipFont,
                                        padding: compact ? '0 3px' : '0 4px'
                                      },
                                      '& .MuiChip-icon': {
                                        fontSize: compact ? '0.875rem' : '0.9rem',
                                        marginLeft: compact ? '2px' : '4px',
                                        marginRight: chipProps.icon ? '-2px' : 0
                                      }
                                    }}
                                  />
                                </Box>
                              );
                            })()
                          )}
                          {displayOptions.includes('3m-avg') && (
                            (() => {
                              if (isForecastMonth(month)) {
                                return (
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.3 }}>
                                    <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>3M:</Typography>
                                    <Chip label="-" size="small" color="default" sx={{ height: `${chipHeight}px`, '& .MuiChip-label': { fontSize: chipFont, padding: compact ? '0 3px' : '0 4px' } }} />
                                  </Box>
                                );
                              }
                              const mi = budgetData.months.indexOf(month);
                              const prev3 = budgetData.months.slice(Math.max(0, mi - 3), mi);
                              const avg = prev3.length > 0 ? prev3.reduce((sum, m) => sum + (budgetData.monthlySummary[m]?.income || 0), 0) / prev3.length : 0;
                              return (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.3 }}>
                                  <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>3M:</Typography>
                                  <Chip label={formatCurrency(avg)} size="small" variant="outlined" sx={{ borderRadius: '14px', height: `${chipHeight}px`, '& .MuiChip-label': { fontSize: chipFont, padding: compact ? '0 6px' : '0 8px' } }} />
                                </Box>
                              );
                            })()
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  );
                })}
              </TotalRow>
            </TableBody>
          </Table>
          </Box>

          {/* EXPENSES SECTION */}
          <Box sx={sectionCardSx}>
          <Table size="small" sx={tableSx}>
            {renderColGroup()}
            <TableHead>
              {renderSectionBand('Expenses', theme.palette.error.main, ArrowDownwardIcon, sortedExpenseCategories.length)}
              {renderMonthHeaderRow('expense', 'Category', 'expense')}
            </TableHead>
            <TableBody>
              {/* EXPENSE CATEGORIES */}
              {displayExpenseCategories.map(category => (
                <TableRow key={`expense-${category}`} hover>
                  <TableCell sx={categoryLabelCellSx(category)}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: valueFontSize, fontWeight: 600 }}>
                        {getCategoryDisplayName(category)}
                      </Typography>
                    </Box>
                  </TableCell>
                  {displayMonths.map((month, index) => {
                    const currentAmount = isForecastMonth(month)
                      ? forecastData.expenseCategories[category]?.[month] || 0
                      : budgetData.expenseCategories[category]?.[month] || 0;
                    return (
                      <TableCell
                        key={`expense-${category}-${month}`}
                        align="right"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCategoryClick(category, month, 'Expense');
                        }}
                        sx={{
                          cursor: 'pointer',
                          borderLeft: monthColBorder,
                          minWidth: monthColWidth,
                          ...currentMonthCellSx(month),
                          ...(isForecastMonth(month)
                            ? { backgroundColor: 'rgba(156, 39, 176, 0.1)' }
                            : {}),
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: valueFontSize }}>
                            {formatCurrency(currentAmount)}
                          </Typography>
                          {/* Display options with labels - more compact */}
                          <Box sx={{ mt: 0.5 }}>
                            {displayOptions.includes('percentages') && budgetData.monthlySummary[month]?.expenses > 0 && (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.3 }}>
                                <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>
                                  Prop%:
                                </Typography>
                                <Chip
                                  label={formatPercentage(currentAmount, budgetData.monthlySummary[month].expenses)}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    borderRadius: '14px',
                                    height: `${chipHeight}px`,
                                    '& .MuiChip-label': {
                                      fontSize: chipFont,
                                      padding: compact ? '0 6px' : '0 8px'
                                    }
                                  }}
                                />
                              </Box>
                            )}
                            {displayOptions.includes('vs-budget') && (
                              (() => {
                                if (isForecastMonth(month)) {
                                  return (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.3 }}>
                                      <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>vs Bud:</Typography>
                                      <Chip label="-" size="small" color="default" sx={{ height: `${chipHeight}px`, '& .MuiChip-label': { fontSize: chipFont, padding: compact ? '0 3px' : '0 4px' } }} />
                                    </Box>
                                  );
                                }
                                const budgetTarget = getBudgetTarget(category, false, month);
                                const variance = budgetTarget === 0
                                  ? (currentAmount > 0 ? '' : '')
                                  : ((currentAmount - budgetTarget) / budgetTarget) * 100;
                                const chipProps = getVarianceChipProps(variance, false);
                                return (
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.3 }}>
                                    <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>
                                      vs Bud:
                                    </Typography>
                                    <Chip
                                      {...chipProps}
                                      size="small"
                                      sx={{
                                        height: `${chipHeight}px`,
                                        '& .MuiChip-label': {
                                          fontSize: chipFont,
                                          padding: compact ? '0 3px' : '0 4px'
                                        },
                                        '& .MuiChip-icon': {
                                          fontSize: compact ? '0.875rem' : '0.9rem',
                                          marginLeft: compact ? '2px' : '4px',
                                          marginRight: chipProps.icon ? '-2px' : 0
                                        }
                                      }}
                                    />
                                  </Box>
                                );
                              })()
                            )}
                            {displayOptions.includes('vs-prior-month') && (
                              (() => {
                                if (isForecastMonth(month)) {
                                  return (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.3 }}>
                                      <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>MoM:</Typography>
                                      <Chip label="-" size="small" color="default" sx={{ height: `${chipHeight}px`, '& .MuiChip-label': { fontSize: chipFont, padding: compact ? '0 3px' : '0 4px' } }} />
                                    </Box>
                                  );
                                }
                                const monthIndex = budgetData.months.indexOf(month);
                                const prevMonth = budgetData.months[monthIndex - 1];
                                if (!prevMonth) return null;
                                const prevAmount = budgetData.expenseCategories[category]?.[prevMonth] || 0;
                                const variance = prevAmount === 0
                                  ? (currentAmount > 0 ? '' : '')
                                  : ((currentAmount - prevAmount) / prevAmount) * 100;
                                const chipProps = getMomChipProps(variance, true);
                                return (
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.3 }}>
                                    <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>
                                      MoM:
                                    </Typography>
                                    <Chip
                                      {...chipProps}
                                      size="small"
                                      sx={{
                                        height: `${chipHeight}px`,
                                        '& .MuiChip-label': {
                                          fontSize: chipFont,
                                          padding: compact ? '0 3px' : '0 4px'
                                        },
                                        '& .MuiChip-icon': {
                                          fontSize: compact ? '0.875rem' : '0.9rem',
                                          marginLeft: compact ? '2px' : '4px',
                                          marginRight: chipProps.icon ? '-2px' : 0
                                        }
                                      }}
                                    />
                                  </Box>
                                );
                              })()
                            )}
                            {displayOptions.includes('3m-avg') && (
                              (() => {
                                if (isForecastMonth(month)) {
                                  return (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.3 }}>
                                      <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>3M:</Typography>
                                      <Chip label="-" size="small" color="default" sx={{ height: `${chipHeight}px`, '& .MuiChip-label': { fontSize: chipFont, padding: compact ? '0 3px' : '0 4px' } }} />
                                    </Box>
                                  );
                                }
                                const monthIndex = budgetData.months.indexOf(month);
                                const prev3 = budgetData.months.slice(Math.max(0, monthIndex - 3), monthIndex);
                                const avg = prev3.length > 0 ? prev3.reduce((sum, m) => sum + (budgetData.expenseCategories[category]?.[m] || 0), 0) / prev3.length : 0;
                                return (
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 0.3 }}>
                                    <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>
                                      3M:
                                    </Typography>
                                    <Chip
                                      label={formatCurrency(avg)}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        borderRadius: '14px',
                                        height: `${chipHeight}px`,
                                        '& .MuiChip-label': {
                                          fontSize: chipFont,
                                          padding: compact ? '0 6px' : '0 8px'
                                        }
                                      }}
                                    />
                                  </Box>
                                );
                              })()
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              {/* EXPENSES TOTAL */}
              <TotalRow>
                <TableCell 
                  sx={{ 
                    fontWeight: 'bold', 
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                    position: 'sticky',
                    left: 0,
                    zIndex: 3,
                    minWidth: '100px',
                    width: '100px',
                    bgcolor: theme.palette.grey[50]
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Total Expenses
                  </Box>
                </TableCell>
                {displayMonths.map((month, index) => {
                  const currentTotalExpenses = isForecastMonth(month)
                    ? forecastData.monthlySummary[month]?.expenses || 0
                    : budgetData.monthlySummary[month]?.expenses || 0;
                  const monthIndex = budgetData.months.indexOf(month);
                  const prevMonth = monthIndex > 0 ? budgetData.months[monthIndex - 1] : null;
                  const prevTotalExpenses = prevMonth ? budgetData.monthlySummary[prevMonth]?.expenses || 0 : 0;
                  const totalExpensesTrend = prevTotalExpenses === 0
                    ? (currentTotalExpenses > 0 ? 100 : 0)
                    : ((currentTotalExpenses - prevTotalExpenses) / prevTotalExpenses) * 100;
                  return (
                    <TableCell
                      key={`total-expenses-${month}`}
                      align="right"
                      sx={{
                        fontWeight: 'bold',
                        borderLeft: monthColBorder,
                        minWidth: '100px',
                        ...currentMonthCellSx(month)
                      }}
                    >
                      <Box>
                        <Typography variant="body2" color="error.main" fontWeight="bold" sx={{ fontSize: valueFontSize }}>
                          {formatCurrency(currentTotalExpenses)}
                        </Typography>
                        {displayOptions.includes('vs-prior-month') && (
                          (() => {
                            if (isForecastMonth(month)) {
                              return (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>MoM:</Typography>
                                  <Chip label="-" size="small" color="default" sx={{ height: `${chipHeight}px`, '& .MuiChip-label': { fontSize: chipFont, padding: compact ? '0 3px' : '0 4px' } }} />
                                </Box>
                              );
                            }
                            const mi = budgetData.months.indexOf(month);
                            const prevMonth = budgetData.months[mi - 1];
                            if (!prevMonth) return null;
                            return (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>MoM:</Typography>
                                <Chip {...getMomChipProps(totalExpensesTrend, true)} size="small" sx={{
                                  height: `${chipHeight}px`,
                                  '& .MuiChip-label': {
                                    fontSize: chipFont,
                                    padding: compact ? '0 3px' : '0 4px'
                                  }
                                }} />
                              </Box>
                            );
                          })()
                        )}
                        {displayOptions.includes('3m-avg') && (
                          (() => {
                            if (isForecastMonth(month)) {
                              return (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>3M:</Typography>
                                  <Chip label="-" size="small" color="default" sx={{ height: `${chipHeight}px`, '& .MuiChip-label': { fontSize: chipFont, padding: compact ? '0 3px' : '0 4px' } }} />
                                </Box>
                              );
                            }
                            const mi = budgetData.months.indexOf(month);
                            const prev3 = budgetData.months.slice(Math.max(0, mi - 3), mi);
                            const avg = prev3.length > 0 ? prev3.reduce((sum, m) => sum + (budgetData.monthlySummary[m]?.expenses || 0), 0) / prev3.length : 0;
                            return (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={metricLabelBaseSx}>3M:</Typography>
                                <Chip label={formatCurrency(avg)} size="small" variant="outlined" sx={{ borderRadius: '14px', height: `${chipHeight}px`, '& .MuiChip-label': { fontSize: chipFont, padding: compact ? '0 6px' : '0 8px' } }} />
                              </Box>
                            );
                          })()
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TotalRow>

              {/* NET INCOME ROW - custom styling for a summary feel */}
              <TableRow>
                  <TableCell sx={{
                      bgcolor: 'grey.800',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: headerFontSize,
                      py: 0.75,
                      borderRight: '1px solid rgba(255, 255, 255, 0.12)',
                      borderBottom: 0,
                      borderBottomLeftRadius: '12px',
                      position: 'sticky',
                      left: 0,
                      zIndex: 3
                  }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccountBalanceIcon sx={{ fontSize: compact ? 16 : 18 }} />
                        Net Income
                      </Box>
                  </TableCell>
                  {displayMonths.map((month, index) => {
                      const netIncome = (isForecastMonth(month) ? forecastData.monthlySummary[month]?.income || 0 : budgetData.monthlySummary[month]?.income || 0) -
                                      (isForecastMonth(month) ? forecastData.monthlySummary[month]?.expenses || 0 : budgetData.monthlySummary[month]?.expenses || 0);
                      return (
                          <TableCell
                              key={`net-income-${month}`}
                              align="right"
                              sx={{
                                  bgcolor: 'grey.800',
                                  color: 'white',
                                  fontWeight: 'bold',
                                  fontSize: headerFontSize,
                                  borderLeft: '1px solid rgba(255, 255, 255, 0.12)',
                                  borderBottom: 0,
                                  ...(index === displayMonths.length - 1 ? { borderBottomRightRadius: '12px' } : {}),
                                  ...currentMonthCellSx(month, theme.palette.grey[800])
                              }}
                          >
                              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: valueFontSize, color: 'inherit' }}>
                                {formatCurrency(netIncome)}
                              </Typography>
                          </TableCell>
                      );
                  })}
              </TableRow>

            </TableBody>
          </Table>
          </Box>

          {/* ACCOUNT BALANCES SECTION */}
          <Box sx={sectionCardSx}>
          <Table size="small" sx={tableSx}>
            {renderColGroup()}
            <TableHead>
              {renderSectionBand('Account Balances', '#03a9f4', AccountBalanceWalletIcon, activeAccounts.length)}
              {renderMonthHeaderRow('balance', 'Account', 'balance')}
            </TableHead>
            <TableBody>
              {/* ACCOUNT BALANCES */}
              {displayAccounts.map(account => (
                <TableRow key={`balance-${account.id}`} hover>
                  <TableCell
                    sx={accountLabelCellSx(account)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: valueFontSize, fontWeight: 600, cursor: 'pointer' }}
                        onClick={() => handleAccountBalanceClick(account.id, displayMonths[displayMonths.length - 1])}
                      >
                        {account.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  {displayMonths.map((month, index) => {
                    let monthBalance = allAccountBalancesByMonth[month]?.[account.id]?.balance ?? 0;
                    const balanceDetails = calculateAccountBalanceDetails(account.id, month);
                    if (isForecastMonth(month) && monthlyForecastBalances && monthlyForecastBalances[month]) {
                      monthBalance = balanceDetails.endingBalance !== undefined ? balanceDetails.endingBalance : account.balance;
                    }
                    return (
                      <TableCell
                        key={`${account.id}-${month}`}
                        align="right"
                        onClick={() => handleAccountBalanceClick(account.id, month)}
                        sx={{
                          fontWeight: 'bold',
                          color: monthBalance < 0 ? 'error.main' : 'text.primary',
                          borderLeft: monthColBorder,
                          minWidth: '100px',
                          ...(isForecastMonth(month) ? { backgroundColor: 'rgba(156, 39, 176, 0.05)' } : {}),
                          ...currentMonthCellSx(month),
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                          }
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: valueFontSize, fontWeight: 600 }}>
                            {formatCurrency(monthBalance)}
                          </Typography>
                          {/* Prop% — this account as a share of net worth */}
                          {displayOptions.includes('percentages') && (
                            (() => {
                              if (isForecastMonth(month)) return <MetricRow label="Prop%:">{dashChip()}</MetricRow>;
                              const nw = getNetWorthFor(month);
                              if (!nw) return null;
                              return <MetricRow label="Prop%:">{outlineChip(formatPercentage(monthBalance, nw))}</MetricRow>;
                            })()
                          )}
                          {/* MoM — vs the previous actual month (works on the first visible column too) */}
                          {displayOptions.includes('vs-prior-month') && (
                            (() => {
                              if (isForecastMonth(month)) return <MetricRow label="MoM:">{dashChip()}</MetricRow>;
                              const pm = prevActualMonth(month);
                              if (!pm) return null;
                              const prevBalance = getAccountBalanceFor(account.id, pm);
                              const variance = prevBalance === 0 ? '' : ((monthBalance - prevBalance) / Math.abs(prevBalance)) * 100;
                              return <MetricRow label="MoM:">{momChip(variance)}</MetricRow>;
                            })()
                          )}
                          {/* 3M — average balance across the prior three actual months */}
                          {displayOptions.includes('3m-avg') && (
                            (() => {
                              if (isForecastMonth(month)) return <MetricRow label="3M:">{dashChip()}</MetricRow>;
                              const prev3 = prev3ActualMonths(month);
                              if (!prev3.length) return null;
                              const avg = prev3.reduce((s, m) => s + getAccountBalanceFor(account.id, m), 0) / prev3.length;
                              return <MetricRow label="3M:">{outlineChip(formatCurrency(avg))}</MetricRow>;
                            })()
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              {/* ACCOUNT TOTAL ROW */}
              <TotalRow last>
                <TableCell 
                  sx={{ 
                    fontWeight: 'bold', 
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                    position: 'sticky',
                    left: 0,
                    zIndex: 3,
                    minWidth: '100px',
                    width: '100px',
                    bgcolor: theme.palette.grey[50]
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Net Worth
                  </Box>
                </TableCell>
                {displayMonths.map((month, index) => {
                  let totalBalance = 0;
                  for (let account of accounts) {
                    let bal = allAccountBalancesByMonth[month]?.[account.id]?.balance ?? 0;
                    totalBalance += bal;
                  }
                  if (isForecastMonth(month) && monthlyForecastBalances && monthlyForecastBalances[month]) {
                    totalBalance = accounts.reduce((sum, account) => {
                      const { endingBalance } = calculateAccountBalanceDetails(account.id, month);
                      return sum + endingBalance;
                    }, 0);
                  }
                  return (
                    <TableCell
                      key={`account-total-${month}`}
                      align="right"
                      sx={{
                        fontWeight: 'bold',
                        color: totalBalance >= 0 ? 'success.main' : 'error.main',
                        borderLeft: monthColBorder,
                        minWidth: '100px',
                        ...(isForecastMonth(month) ? { backgroundColor: 'rgba(156, 39, 176, 0.1)' } : {}),
                        ...currentMonthCellSx(month),
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: valueFontSize }}>
                          {formatCurrency(totalBalance)}
                        </Typography>
                        {/* MoM — vs the previous actual month (works on the first visible column too) */}
                        {displayOptions.includes('vs-prior-month') && (
                          (() => {
                            if (isForecastMonth(month)) return <MetricRow label="MoM:">{dashChip()}</MetricRow>;
                            const pm = prevActualMonth(month);
                            if (!pm) return null;
                            const prevTotal = getNetWorthFor(pm);
                            const variance = prevTotal === 0 ? '' : ((totalBalance - prevTotal) / Math.abs(prevTotal)) * 100;
                            return <MetricRow label="MoM:">{momChip(variance)}</MetricRow>;
                          })()
                        )}
                        {/* 3M — average net worth across the prior three actual months */}
                        {displayOptions.includes('3m-avg') && (
                          (() => {
                            if (isForecastMonth(month)) return <MetricRow label="3M:">{dashChip()}</MetricRow>;
                            const prev3 = prev3ActualMonths(month);
                            if (!prev3.length) return null;
                            const avg = prev3.reduce((s, m) => s + getNetWorthFor(m), 0) / prev3.length;
                            return <MetricRow label="3M:">{outlineChip(formatCurrency(avg))}</MetricRow>;
                          })()
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TotalRow>
            </TableBody>
          </Table>
          </Box>
        </Box>
        {/* Floating horizontal scrollbar: fixed just above the bottom edge of the
            viewport whenever the tables are on screen — faint at rest, solid on
            hover — so panning is always within reach without scrolling the page */}
        <Box
          ref={floatScrollbarRef}
          sx={{
            position: 'fixed',
            bottom: 16,
            left: 0,
            zIndex: 1200,
            height: '14px',
            borderRadius: '7px',
            backgroundColor: alpha(theme.palette.grey[600], 0.22),
            boxShadow: theme.shadows[2],
            opacity: 0,
            pointerEvents: 'none',
            transition: 'opacity 0.25s ease',
            overflowX: 'auto',
            overflowY: 'hidden',
            ...slimScrollbarSx,
            scrollbarColor: `${theme.palette.grey[500]} transparent`,
            '&::-webkit-scrollbar-thumb': { backgroundColor: theme.palette.grey[500], borderRadius: '8px' },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: theme.palette.grey[600] },
          }}
        >
          <Box ref={floatScrollbarInnerRef} sx={{ height: '1px' }} />
        </Box>
      </Box>
    </Box>
  );
};

export default BudgetTableView;
